/* WCAG contrast helpers for the restaurant-configurable menu header
   (logo_image / header_bg_color / header_text_color). Mirrors the
   already-proven math in backend/admin/src/utils/qrColor.js — the two
   SPAs don't share code, so this is a deliberate small duplication of
   the same algorithm rather than an import. */

const AA_MIN_CONTRAST_RATIO = 4.5;

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;

  const num = parseInt(full, 16);

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function channelLuminance(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (
    0.2126 * channelLuminance(r) +
    0.7152 * channelLuminance(g) +
    0.0722 * channelLuminance(b)
  );
}

export function contrastRatio(hexA, hexB) {
  const lA = relativeLuminance(hexA) + 0.05;
  const lB = relativeLuminance(hexB) + 0.05;
  return lA > lB ? lA / lB : lB / lA;
}

/** Near-black or near-white, whichever reads better on top of bgHex. */
export function pickReadableTextColor(bgHex) {
  return relativeLuminance(bgHex) > 0.5 ? "#16181d" : "#ffffff";
}

/** Returns textHex if it's readable on bgHex, otherwise a safe fallback. */
export function ensureReadableTextColor(bgHex, textHex) {
  if (!bgHex || !textHex) return textHex;

  try {
    if (contrastRatio(bgHex, textHex) >= AA_MIN_CONTRAST_RATIO) {
      return textHex;
    }
  } catch {
    return textHex;
  }

  return pickReadableTextColor(bgHex);
}

function clampChannel(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function mix(hexA, hexB, weight) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);

  const channel = (key) => clampChannel(a[key] + (b[key] - a[key]) * weight);

  return `#${["r", "g", "b"]
    .map((key) => channel(key).toString(16).padStart(2, "0"))
    .join("")}`;
}

/** Blends hex toward white by `amount` (0-1). */
export function lighten(hex, amount) {
  return mix(hex, "#ffffff", amount);
}

/** Blends hex toward black by `amount` (0-1). */
export function darken(hex, amount) {
  return mix(hex, "#000000", amount);
}

export function hexToRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Derives the full brand-color token set (buttons, active pill/badge
 * backgrounds, focus ring) from the single accent color a restaurant
 * picks in admin — deliberately one input, not five, so owners aren't
 * asked to design a palette themselves. See styles/tokens.css for the
 * token set this mirrors/overrides.
 */
export function deriveAccentTokens(accentHex) {
  if (!accentHex || !/^#[0-9A-Fa-f]{6}$/.test(accentHex)) return null;

  return {
    "--color-accent": accentHex,
    "--color-accent-hover": darken(accentHex, 0.08),
    "--color-accent-active": darken(accentHex, 0.16),
    "--color-accent-subtle": lighten(accentHex, 0.85),
    "--color-accent-contrast": pickReadableTextColor(accentHex),
    "--focus-ring-color": hexToRgba(accentHex, 0.4),
  };
}
